/**
 * Presenca no lobby: quanta gente esta disponivel para jogar cada jogo.
 *
 * O numero vem do servidor, que o calcula a partir da fila de matchmaking
 * e das partidas em andamento. Nao existe numero inventado, piso minimo
 * nem multiplicador: num produto onde a quantidade de gente jogando
 * influencia a decisao de apostar, contador fabricado e propaganda
 * enganosa.
 *
 * Por isso os estados abaixo separam "ninguem jogando" de "nao consegui
 * consultar". Mostrar zero quando a consulta falhou seria mentir para o
 * lado oposto, e igualmente errado.
 */
import type { GameType } from './adapter.ts';

export interface PresencaJogo {
  readonly gameType: GameType;
  readonly naFila: number;
  readonly emPartida: number;
  readonly disponiveis: number;
}

export type Presenca =
  | { readonly estado: 'carregando' }
  /** Sem backend configurado: a demonstracao roda so no navegador. */
  | { readonly estado: 'demonstracao' }
  | { readonly estado: 'indisponivel' }
  | { readonly estado: 'ok'; readonly jogos: readonly PresencaJogo[] };

interface RespostaBruta {
  games?: {
    game_type?: unknown;
    na_fila?: unknown;
    em_partida?: unknown;
    disponiveis?: unknown;
  }[];
}

const JOGOS_VALIDOS = new Set<string>(['CHESS', 'CHECKERS', 'TIC_TAC_TOE']);

function inteiroNaoNegativo(valor: unknown): number | null {
  return typeof valor === 'number' && Number.isInteger(valor) && valor >= 0 ? valor : null;
}

/**
 * Converte a resposta do servidor, descartando o que nao for numero
 * inteiro nao negativo. Payload torto vira ausencia de dado, nunca um
 * numero improvisado.
 */
export function interpretarPresenca(payload: unknown): Presenca {
  if (typeof payload !== 'object' || payload === null) return { estado: 'indisponivel' };

  const bruto = payload as RespostaBruta;
  if (!Array.isArray(bruto.games)) return { estado: 'indisponivel' };

  const jogos: PresencaJogo[] = [];
  for (const linha of bruto.games) {
    const tipo = linha.game_type;
    if (typeof tipo !== 'string' || !JOGOS_VALIDOS.has(tipo)) continue;

    const naFila = inteiroNaoNegativo(linha.na_fila);
    const emPartida = inteiroNaoNegativo(linha.em_partida);
    const disponiveis = inteiroNaoNegativo(linha.disponiveis);
    if (naFila === null || emPartida === null || disponiveis === null) continue;

    jogos.push({ gameType: tipo as GameType, naFila, emPartida, disponiveis });
  }

  return jogos.length > 0 ? { estado: 'ok', jogos } : { estado: 'indisponivel' };
}

export function presencaDoJogo(presenca: Presenca, gameType: GameType): PresencaJogo | null {
  if (presenca.estado !== 'ok') return null;
  return presenca.jogos.find((j) => j.gameType === gameType) ?? null;
}

/** Endereco da API. Ausente significa demonstracao, sem servidor. */
export function baseDaApi(): string | null {
  const bruto = import.meta.env.VITE_API_BASE_URL;
  return typeof bruto === 'string' && bruto.trim() !== '' ? bruto.replace(/\/+$/, '') : null;
}

export async function buscarPresenca(
  stakeMode: 'REAL' | 'VIRTUAL',
  sinal?: AbortSignal,
): Promise<Presenca> {
  const base = baseDaApi();
  if (!base) return { estado: 'demonstracao' };

  try {
    const resposta = await fetch(`${base}/api/lobby?stakeMode=${stakeMode}`, {
      ...(sinal ? { signal: sinal } : {}),
      headers: { accept: 'application/json' },
    });
    if (!resposta.ok) return { estado: 'indisponivel' };
    return interpretarPresenca(await resposta.json());
  } catch {
    return { estado: 'indisponivel' };
  }
}
